import React, { useState, useEffect } from 'react';
import {
  Eye,
  TrendingUp,
  Activity,
  CheckCircle,
  RefreshCw,
  Target,
  BarChart3,
  Calendar,
  User,
  Shield,
  FileText,
  Clock,
  Award
} from 'lucide-react';

interface AuditorEntryViewProps {
  companyId?: number;
  frameworkId?: string;
}

interface AuditLogEntry {
  id: number;
  tenantId: number;
  userId: number;
  action: string;
  resource: string;
  resourceId: string;
  details: any;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
}

interface ComplianceReadiness {
  totalActions: number;
  complianceActions: number;
  evidenceUploads: number;
  approvals: number;
  rejections: number;
  lastActivity: string;
  readinessScore: number;
}

const AuditorEntryView: React.FC<AuditorEntryViewProps> = ({
  companyId,
  frameworkId
}) => {
  const [activeTab, setActiveTab] = useState<'readiness' | 'activity' | 'traceability'>('readiness');
  const [loading, setLoading] = useState(true);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [readiness, setReadiness] = useState<ComplianceReadiness | null>(null);

  useEffect(() => {
    loadAuditorData();
  }, [companyId, frameworkId]);

  const loadAuditorData = async () => {
    try {
      setLoading(true);

      // Load tenant audit logs
      const auditResponse = await fetch('/api/tenant/audit-logs', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (auditResponse.ok) {
        const result = await auditResponse.json();
        const logs = result.logs || [];
        setAuditLogs(logs);

        // Calculate readiness from audit logs
        const readinessData = calculateReadinessFromLogs(logs);
        setReadiness(readinessData);
      }
    } catch (error) {
      console.error('Failed to load auditor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateReadinessFromLogs = (logs: AuditLogEntry[]): ComplianceReadiness => {
    const totalActions = logs.length;
    const complianceActions = logs.filter(log =>
      log.action.includes('compliance') ||
      log.action.includes('evidence') ||
      log.action.includes('approval')
    ).length;

    const evidenceUploads = logs.filter(log =>
      log.action.includes('upload') || log.action.includes('evidence')
    ).length;

    const approvals = logs.filter(log =>
      log.action === 'approved' || log.details?.status === 'approved'
    ).length;

    const rejections = logs.filter(log =>
      log.action === 'rejected' || log.details?.status === 'rejected'
    ).length;

    const lastActivity = logs.length > 0 && logs[0]?.createdAt
      ? new Date(logs[0].createdAt).toISOString()
      : new Date().toISOString();

    // Calculate readiness score based on activity patterns
    const readinessScore = Math.min(100, Math.round(
      (complianceActions / Math.max(totalActions, 1)) * 60 +
      (evidenceUploads / Math.max(totalActions, 1)) * 30 +
      (approvals / Math.max(totalActions + rejections, 1)) * 10
    ));

    return {
      totalActions,
      complianceActions,
      evidenceUploads,
      approvals,
      rejections,
      lastActivity,
      readinessScore
    };
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getReadinessColor = (score: number) => {
    if (score >= 80) return 'from-emerald-500 to-green-600';
    if (score >= 60) return 'from-amber-500 to-orange-600';
    if (score >= 40) return 'from-orange-500 to-red-600';
    return 'from-red-500 to-red-700';
  };

  const getReadinessStatus = (score: number) => {
    if (score >= 80) return { text: 'Audit Ready', color: 'text-emerald-600', bg: 'bg-emerald-100' };
    if (score >= 60) return { text: 'Good Progress', color: 'text-amber-600', bg: 'bg-amber-100' };
    if (score >= 40) return { text: 'Needs Improvement', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { text: 'Critical Gaps', color: 'text-red-600', bg: 'bg-red-100' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="text-slate-600 animate-spin" size={48} />
          <p className="text-slate-600 font-medium">Loading auditor view...</p>
        </div>
      </div>
    );
  }

  const status = readiness ? getReadinessStatus(readiness.readinessScore) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="bg-slate-700 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Eye className="text-white" size={32} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Auditor Entry View</h1>
                  <p className="text-slate-100 mt-1">Compliance assessment and audit trail analysis</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-right">
                  <div className="text-sm text-slate-300">Company ID</div>
                  <div className="text-lg font-semibold text-white">{companyId || 'N/A'}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-300">Framework</div>
                  <div className="text-lg font-semibold text-white">{frameworkId || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="border-b border-slate-100">
            <nav className="flex -mb-px">
              {[
                { id: 'readiness', label: 'Compliance Readiness', icon: Target, color: 'text-slate-600' },
                { id: 'activity', label: 'Activity Summary', icon: BarChart3, color: 'text-emerald-600' },
                { id: 'traceability', label: 'Audit Trail', icon: Activity, color: 'text-purple-600' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 py-4 px-6 border-b-2 font-medium text-sm transition-all ${
                    activeTab === tab.id
                      ? 'border-slate-500 text-slate-600 bg-slate-50'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <tab.icon size={18} className={activeTab === tab.id ? 'text-slate-600' : tab.color} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Tab 1: Compliance Readiness */}
          {activeTab === 'readiness' && readiness && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <Target className="text-slate-600" size={24} />
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Compliance Readiness Assessment</h2>
                    <p className="text-sm text-slate-600">Overall compliance status and readiness score</p>
                  </div>
                </div>
              </div>

              <div className="p-8">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full mb-6">
                    <Award className="text-slate-600" size={16} />
                    <span className="text-slate-800 font-medium">READINESS SCORE</span>
                  </div>

                  <div className={`inline-flex items-center justify-center w-40 h-40 rounded-full bg-gradient-to-r ${getReadinessColor(readiness.readinessScore)} mb-6 shadow-lg`}>
                    <span className="text-4xl font-bold text-white">{readiness.readinessScore}%</span>
                  </div>

                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {status?.text}
                  </h3>
                  <p className="text-slate-600">
                    Based on {readiness.totalActions} total activities logged
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <CheckCircle className="text-blue-600" size={20} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Compliance Actions</h4>
                        <p className="text-sm text-slate-600">of {readiness.totalActions} total</p>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-blue-600">{readiness.complianceActions}</div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <FileText className="text-emerald-600" size={20} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Evidence Uploads</h4>
                        <p className="text-sm text-slate-600">documents processed</p>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-emerald-600">{readiness.evidenceUploads}</div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <CheckCircle className="text-green-600" size={20} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Approvals</h4>
                        <p className="text-sm text-slate-600">records approved</p>
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-green-600">{readiness.approvals}</div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-slate-100 rounded-lg">
                        <Calendar className="text-slate-600" size={20} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Last Activity</h4>
                        <p className="text-sm text-slate-600">most recent action</p>
                      </div>
                    </div>
                    <div className="text-lg font-bold text-slate-900">{formatDate(readiness.lastActivity)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 2: Activity Summary */}
          {activeTab === 'activity' && readiness && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <BarChart3 className="text-emerald-600" size={24} />
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Activity Summary</h2>
                    <p className="text-sm text-slate-600">Detailed breakdown of compliance activities</p>
                  </div>
                </div>
              </div>

              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Activity Breakdown */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-2 mb-6">
                      <TrendingUp className="text-slate-600" size={20} />
                      <h3 className="text-lg font-semibold text-slate-900">Activity Breakdown</h3>
                    </div>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-3 border-b border-slate-200">
                        <span className="text-slate-700 font-medium">Total Actions</span>
                        <span className="text-2xl font-bold text-slate-900">{readiness.totalActions}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-slate-200">
                        <span className="text-slate-700 font-medium">Compliance Related</span>
                        <span className="text-2xl font-bold text-blue-600">{readiness.complianceActions}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-slate-200">
                        <span className="text-slate-700 font-medium">Evidence Uploads</span>
                        <span className="text-2xl font-bold text-emerald-600">{readiness.evidenceUploads}</span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b border-slate-200">
                        <span className="text-slate-700 font-medium">Approvals</span>
                        <span className="text-2xl font-bold text-green-600">{readiness.approvals}</span>
                      </div>
                      <div className="flex justify-between items-center py-3">
                        <span className="text-slate-700 font-medium">Rejections</span>
                        <span className="text-2xl font-bold text-red-600">{readiness.rejections}</span>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity Timeline */}
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-2 mb-6">
                      <Clock className="text-slate-600" size={20} />
                      <h3 className="text-lg font-semibold text-slate-900">Recent Activity</h3>
                    </div>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {auditLogs.slice(0, 10).map((log) => (
                        <div key={log.id} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200">
                          <div className="p-2 bg-slate-100 rounded-lg mt-0.5">
                            <Activity className="text-slate-600" size={14} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium text-slate-900 text-sm">{log.action}</span>
                              <span className="text-xs text-slate-500">{formatDate(log.createdAt)}</span>
                            </div>
                            <p className="text-xs text-slate-600 truncate">Resource: {log.resource}</p>
                          </div>
                        </div>
                      ))}
                      {auditLogs.length === 0 && (
                        <div className="text-center py-8 text-slate-500">
                          <Activity className="mx-auto mb-2" size={24} />
                          <p>No recent activity</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab 3: Audit Trail */}
          {activeTab === 'traceability' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <Activity className="text-purple-600" size={24} />
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900">Audit Trail</h2>
                    <p className="text-sm text-slate-600">Complete log of system activities and changes</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {auditLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <Activity className="text-slate-400 mb-4" size={64} />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No audit logs found</h3>
                    <p className="text-slate-600 max-w-md">Audit logs will appear here as system activities are recorded.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {auditLogs.map((log) => (
                      <div key={log.id} className="p-6 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-slate-100 rounded-xl">
                            <Activity className="text-slate-600" size={20} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                {log.action.toUpperCase()}
                              </span>
                              <span className="text-sm text-slate-600 font-medium">{log.resource}</span>
                              <span className="text-xs text-slate-400">ID: {log.resourceId}</span>
                            </div>

                            {log.details && (
                              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-3">
                                <div className="text-xs font-medium text-slate-700 mb-2">Details:</div>
                                <pre className="text-xs text-slate-600 whitespace-pre-wrap font-mono">
                                  {typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
                                </pre>
                              </div>
                            )}

                            <div className="flex items-center gap-6 text-xs text-slate-500">
                              <div className="flex items-center gap-1">
                                <User size={12} />
                                <span>User ID: {log.userId}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Shield size={12} />
                                <span>IP: {log.ipAddress}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar size={12} />
                                <span>{formatDate(log.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditorEntryView;